import Button from '@/shared/Button'
import FormAutocomplete from '@/shared/FormCreator/FormItem/FormAutocomplete'

import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Popper from '@mui/material/Popper'

import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'

import Box from '@mui/material/Box'

import HighlightOff from '@mui/icons-material/HighlightOff'
import IconButton from '@mui/material/IconButton'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import debounce from '@mui/material/utils/debounce'

import ConfirmDialog from '@/shared/ConfirmDialog'
import { Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import { alpha } from '@mui/material/styles'
import usePermissions from '@/hooks/usePermissions'

const mapStateToProps = ({ loading }) => ({
  isLoading: loading['user/editUserGroup'] || loading['userGroup/createUserGroup'],
})

const Selection = ({ data, onRemove }) => (
  <Chip
    icon={
      <IconButton
        size="small"
        sx={{ padding: 0 }}
        onClick={() => {
          onRemove(data?.id)
        }}
      >
        <HighlightOff />
      </IconButton>
    }
    label={data?.label}
  />
)

const EditUserGroupDialog = ({ dispatch, isLoading, onClose, onComplete, data, ...props }) => {
  const { t } = useTranslation()

  const searchInputRef = useRef()

  const [searchInput, setSearchInput] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [selected, setSelected] = useState([])
  const [selectedInput, setSelectedInput] = useState()
  const canCreateGroup = usePermissions('userGroup_create')

  const handleClose = () => {
    setSelected([])
    setSearchInput('')
    setSearchResults([])
    searchInputRef.current = ''
    onClose?.()
  }

  const handleSubmit = () => {
    const postData = selected.map(s => s.id)
    dispatch({
      type: 'user/editUserGroup',
      payload: { data: postData, userId: data.userId },
      successCallback: () => {
        dispatch({
          type: 'app/notify',
          payload: { message: t('messages.user_updated'), severity: 'success' },
        })
        onComplete?.()
        handleClose?.()
      },
    })
  }

  const handleSelectionRemove = id => {
    setSelected(selected.filter(s => s.id !== id))
  }

  const createUserGroup = () => {
    dispatch({
      type: 'userGroup/createUserGroup',
      payload: { groupName: searchInputRef.current, groupDesc: '' },
      successCallback: result => {
        const { groupId, groupName } = result
        setSelected([{ id: groupId, label: groupName }, ...selected])
      },
    })
  }

  const handleAddGroup = () => {
    const existing = searchResults.find(
      res => res.label.toLowerCase() === searchInputRef.current.toLowerCase(),
    )

    if (existing) {
      setSelected([existing, ...selected])
    } else {
      createUserGroup()
    }
  }

  const fetchData = useCallback(() => {
    if (searchInputRef.current) {
      dispatch({
        type: 'userGroup/getUserGroupsByName',
        payload: { searchUserGroupName: searchInputRef.current },
        successCallback: options => {
          const transformedOptions = Object.keys(options).map(key => ({
            label: options[key],
            id: key,
          }))
          if (transformedOptions) {
            setSearchResults(transformedOptions)
          }
        },
      })
    } else {
      setSearchResults([])
    }
  }, [dispatch])

  const fetchDataDebounce = useMemo(
    () =>
      debounce(() => {
        fetchData()
      }, 400),
    [fetchData],
  )

  useEffect(() => {
    fetchDataDebounce()
  }, [searchInput, fetchDataDebounce])

  useEffect(() => {
    if (data) {
      const { userGroupMaps } = data

      const newSelected = []

      Object.keys(userGroupMaps).forEach(key => {
        newSelected.push({ id: key, label: userGroupMaps[key] })
      })

      setSelected(newSelected)
    }
  }, [data])

  const canAdd = () => {
    const alreadySelected = selected.some(s => s.label === searchInput || s.label === selectedInput)

    if (!canCreateGroup && !selectedInput) {
      return false
    }

    if (alreadySelected || !searchInput) {
      return false
    }

    return true
  }

  return (
    <ConfirmDialog
      {...props}
      title={t('general.edit_registered_user_group')}
      open={!!data}
      onClose={handleClose}
      onSubmit={() => {
        handleSubmit()
      }}
      loading={isLoading}
      contentProps={{ sx: { width: 500 } }}
    >
      <Typography fontWeight="bold" mb={2}>{`${t('general.user_group_name')}:`}</Typography>
      <Stack mb={10} direction="row" justifyContent="flex-start">
        <Box>
          <FormAutocomplete
            freeSolo={canCreateGroup}
            inputConfig={{
              onInputChange: (_, value, reason) => {
                searchInputRef.current = value

                if (['input', 'clear'].includes(reason)) {
                  setSearchInput(value)
                }

                if (reason === 'reset') {
                  setSelectedInput(value)
                }

                if (reason === 'clear') {
                  setSelectedInput()
                }
              },
              sx: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
                '& .Mui-focused': { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${alpha('#000', 0.25)} !important`,
                  borderWidth: '1px !important',
                  boxShadow: 'none !important',
                },
              },
            }}
            options={searchResults}
            getOptionDisabled={option => selected.some(s => s.id === option.id)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            PopperComponent={popperProps => (
              <Popper
                elevation={0}
                {...popperProps}
                componentsProps={{ sx: { backgroundColor: 'green' } }}
                sx={{
                  '& .MuiPaper-root': {
                    borderTopRightRadius: 0,
                    borderTopLeftRadius: 0,
                    boxShadow: 0,
                    border: `1px solid ${alpha('#000', 0.25)}`,
                  },
                }}
              />
            )}
          />
        </Box>
        <Button
          sx={{ ml: 2 }}
          variant="outlined"
          color="primary"
          disabled={!canAdd() || isLoading}
          loading={isLoading}
          onClick={() => {
            handleAddGroup()
          }}
        >
          {t('general.add')}
        </Button>
      </Stack>

      <Card variant="outlined" sx={{ borderRadius: 5 }}>
        <CardContent sx={{ minHeight: 100 }}>
          <Stack direction="row" flexWrap="wrap" gap={2}>
            {selected.map(obj => (
              <Selection key={obj?.id} data={obj} onRemove={handleSelectionRemove} />
            ))}
          </Stack>
        </CardContent>
      </Card>
    </ConfirmDialog>
  )
}

export default connect(mapStateToProps)(EditUserGroupDialog)
